import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Compass, Shield, Settings, TrendingUp, CheckCircle, Zap, Focus, Flame } from 'lucide-react';
import { IMAGES, bgStyle } from '@/config/images';

const LaserMethodology = () => {
  const focuses = [
    {
      icon: Shield,
      title: 'Prioritizing safety and material compatibility',
      description: 'Ensuring proper ventilation, avoiding hazardous materials, and protecting equipment'
    },
    {
      icon: Focus,
      title: 'Working within real laser limitations',
      description: 'Understanding your wattage, focal length, and bed size—not theoretical maximums'
    },
    {
      icon: Zap,
      title: 'Supporting CO2, diode, and fiber lasers',
      description: 'Tailored guidance for hobbyist through professional-grade machines'
    },
    {
      icon: TrendingUp,
      title: 'Encouraging test-first approaches',
      description: 'Progress through material samples, power tests, and focus calibration—not guesswork'
    }
  ];

  return (
    <div className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6" style={bgStyle(IMAGES.METHODOLOGY)}>
      <div className="fixed inset-0 bg-black/90 -z-10" style={{ pointerEvents: 'none' }} />
    <div className="max-w-4xl mx-auto space-y-8 p-4" data-testid="laser-methodology-page">
      {/* Page Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center gap-2 px-4 py-2 mb-4 border border-orange-500/50 rounded-sm bg-orange-500/10">
          <Compass className="w-5 h-5 text-orange-500" />
          <span className="text-sm font-bold text-orange-500 uppercase tracking-wide">Our Approach</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-bold text-white uppercase tracking-tight mb-4" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
          Our Methodology
        </h1>
      </div>

      {/* Main Content Card */}
      <Card className="bg-[#242424] border-[#3a3a3a]">
        <CardContent className="pt-6 space-y-6">
          {/* Introduction */}
          <div className="prose prose-invert max-w-none">
            <p className="text-base sm:text-lg text-[#d6d6d6] leading-relaxed">
              Laser Cutting Helpdesk is designed around how laser systems are <span className="text-orange-500 font-semibold">actually built and used</span> — not how they appear in ideal conditions or spec sheets.
            </p>
            
            <p className="text-[#a3a3a3] leading-relaxed mt-4">
              The system asks structured, practical questions about your laser configuration, material properties, and project goals. Where information is missing or unclear, Laser Cutting Helpdesk applies conservative, experience-based defaults that prioritize safety, clean cuts, and equipment longevity.
            </p>
          </div>

          {/* Recommendations Focus */}
          <div className="mt-8">
            <h2 className="text-lg font-bold text-white uppercase tracking-wide mb-4" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Recommendations Focus On:
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {focuses.map((focus, index) => (
                <div 
                  key={index}
                  className="flex items-start gap-3 p-4 bg-[#1f1f1f] border border-[#3a3a3a] rounded-sm hover:border-orange-500/30 transition-colors"
                >
                  <div className="w-10 h-10 bg-orange-500/10 border border-orange-500/30 rounded-full flex items-center justify-center flex-shrink-0">
                    <focus.icon className="w-5 h-5 text-orange-500" />
                  </div>
                  <div>
                    <h3 className="text-white font-semibold text-sm mb-1">{focus.title}</h3>
                    <p className="text-[#a3a3a3] text-xs">{focus.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Experience Statement */}
          <div className="mt-8 p-5 bg-[#1f1f1f] border-l-4 border-orange-500 rounded-r-sm">
            <p className="text-[#d6d6d6] leading-relaxed">
              This approach reflects <span className="text-white font-semibold">hands-on laser cutting experience</span>, extensive material testing, power calibrations, and real-world troubleshooting — where clean cuts and engravings are achieved through careful testing and refinement.
            </p>
          </div>

          {/* Closing Statement */}
          <div className="mt-6 flex items-start gap-3 p-4 bg-orange-500/5 border border-orange-500/20 rounded-sm">
            <CheckCircle className="w-6 h-6 text-orange-500 flex-shrink-0 mt-0.5" />
            <p className="text-[#d6d6d6]">
              <span className="text-white font-semibold">Laser Cutting Helpdesk provides a reliable starting point</span> that users can adapt as their machines, materials, and expertise evolve.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
    </div>
  );
};

export default LaserMethodology;
